import React, { useState } from 'react';
import { Box } from '@mui/system';
import { Dayjs } from 'dayjs';
import { EsgDatePickerField } from 'components';
import { getDateFormatset } from 'utils';

interface DateRangePickerProps {
    viewFromDate: Dayjs | null;
    viewToDate: Dayjs | null;
    onFromDateChange: (date: Dayjs | null) => void;
    onToDateChange: (date: Dayjs | null) => void;
    fromLabel?: string;
    toLabel?: string;
    fromPlaceholder?: string;
    toPlaceholder?: string;
    format?: string;
    enableValidation?: boolean;
    errorMessage?: string;
    containerSx?: any;
    fieldSx?: any;
    gap?: string | number;
    maxWidth?: string | number;
    showErrorOnToField?: boolean;
    showTitle?: boolean;
    title?: string;
    titleComponent?: React.ReactNode;
    direction?: 'row' | 'column';
    fromError?: string;
    toError?: string;
    fromFieldProps?: any;
    toFieldProps?: any;
    views?: ('year' | 'month' | 'day')[];
    openTo?: 'year' | 'month' | 'day';
}

export const DateRangePicker = ({
    viewFromDate,
    viewToDate,
    onFromDateChange,
    onToDateChange,
    fromLabel = "View from",
    toLabel = "View to",
    fromPlaceholder,
    toPlaceholder,
    format = getDateFormatset('/'),
    enableValidation = true,
    errorMessage,
    containerSx,
    fieldSx,
    gap = '12px',
    maxWidth = '352px',
    showErrorOnToField = true,
    showTitle = false,
    title = "Select Date Range",
    titleComponent,
    direction = 'row',
    fromError,
    toError,
    fromFieldProps = {},
    toFieldProps = {},
    views = ['year', 'month', 'day'],
    openTo = 'year',
}: DateRangePickerProps) => {
    const [internalDateError, setInternalDateError] = useState<string>('');

    const validateDates = (fromDate: Dayjs | null, toDate: Dayjs | null) => {
        if (!enableValidation || !fromDate || !toDate) {
            setInternalDateError('');
            return;
        }

        if (toDate.isBefore(fromDate)) {
            setInternalDateError(errorMessage || 'The end date must be after the start date.');
        } else {
            setInternalDateError('');
        }
    };

    const handleFromDateChange = (date: Dayjs | null) => {
        onFromDateChange(date);
        if (enableValidation) {
            validateDates(date, viewToDate);
        }
    };

    const handleToDateChange = (date: Dayjs | null) => {
        onToDateChange(date);
        if (enableValidation) {
            validateDates(viewFromDate, date);
        }
    };

    const finalFromError = fromError || (showErrorOnToField ? '' : internalDateError);
    const finalToError = toError || (showErrorOnToField ? internalDateError : '');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth, ...containerSx }}>
            {showTitle && (titleComponent || <div>{title}</div>)}
            <Box sx={{ display: 'flex', gap: direction === 'column' ? '6px' : gap, flexDirection: direction }}>
                <EsgDatePickerField
                    views={views}
                    placeholdervalue={fromPlaceholder || format}
                    format={format}
                    openTo={openTo}
                    onChange={handleFromDateChange}
                    value={viewFromDate}
                    label={fromLabel}
                    clearDate={false}
                    textFieldProps={{
                        sx: fieldSx,
                        error: !!finalFromError,
                        helperText: finalFromError,
                        ...fromFieldProps,
                    }}
                />
                <EsgDatePickerField
                    views={views}
                    placeholdervalue={toPlaceholder || format}
                    format={format}
                    openTo={openTo}
                    onChange={handleToDateChange}
                    value={viewToDate}
                    label={toLabel}
                    clearDate={false}
                    textFieldProps={{
                        sx: fieldSx,
                        error: !!finalToError,
                        helperText: finalToError,
                        ...toFieldProps,
                    }}
                />
            </Box>
        </Box>
    );
}; 